import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Modal } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand } from '@fortawesome/free-solid-svg-icons';
import '../../../theme/Layout/ChartConfig';
import {
  ChartCard,
  ChartCardHeader,
  ChartCardTitle,
  ChartCardSubtitle,
  ChartCardActions,
  ExpandButton,
  RevenueLegendRow,
  RevenueLegendItem,
  RevenueLegendLabelRow,
  RevenueSwatch,
  RevenueLegendLabel,
  RevenueLegendValue,
  ChartCanvasContainer,
  ModalChartContainer,
  ModalSubtitle,
  StyledSelect,
} from './Styles';
import { PERIOD_FILTER_OPTIONS } from './Constant';
import PlatformFilterDropdown from './PlatformFilterDropdown';
import type { RevenueCardProps } from './types';

const RevenueCard: React.FC<RevenueCardProps> = ({ labels, series, isDarkMode = false }) => {
  const [period, setPeriod] = useState('monthly');
  const [channel, setChannel] = useState('all');
  const [expanded, setExpanded] = useState(false);

  const gridColor = isDarkMode ? '#2d3748' : '#f0f0f0';
  const tickColor = isDarkMode ? '#a0aec0' : '#718096';

  const chartData = {
    labels,
    datasets: series.map((s) => ({
      label: s.label,
      data: s.data,
      borderColor: s.color,
      backgroundColor: (context: any) => {
        const { ctx, chartArea } = context.chart;
        if (!chartArea) return `${s.color}20`;
        const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        gradient.addColorStop(0, `${s.color}40`);
        gradient.addColorStop(1, `${s.color}00`);
        return gradient;
      },
      fill: true,
      tension: 0.4,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: s.color,
      borderWidth: 2,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: isDarkMode ? '#1a202c' : '#fff',
        titleColor: isDarkMode ? '#fff' : '#1a202c',
        bodyColor: isDarkMode ? '#fff' : '#1a202c',
        borderColor: isDarkMode ? '#4a5568' : '#e2e8f0',
        borderWidth: 1,
        callbacks: {
          label: (context: any) => `${context.dataset.label}: $${context.formattedValue}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: tickColor, font: { size: 11 } },
      },
      y: {
        grid: { color: gridColor },
        ticks: { color: tickColor, font: { size: 11 }, callback: (v: number) => `$${v}` },
      },
    },
  };

  return (
    <ChartCard>
      <ChartCardHeader>
        <div>
          <ChartCardTitle>Revenue</ChartCardTitle>
          <ChartCardSubtitle>Revenue vs refunds over time</ChartCardSubtitle>
        </div>
        <ChartCardActions>
          <ExpandButton aria-label="Expand chart" onClick={() => setExpanded(true)}>
            <FontAwesomeIcon icon={faExpand} />
          </ExpandButton>
          <PlatformFilterDropdown value={channel} onChange={setChannel} />
          <StyledSelect
            value={period}
            onChange={setPeriod}
            options={PERIOD_FILTER_OPTIONS}
            dropdownStyle={{ borderRadius: 12, overflow: 'hidden' }}
          />
        </ChartCardActions>
      </ChartCardHeader>

      <RevenueLegendRow>
        {series.map((s) => (
          <RevenueLegendItem key={s.label}>
            <RevenueLegendLabelRow>
              <RevenueSwatch color={s.color} />
              <RevenueLegendLabel>{s.label}</RevenueLegendLabel>
            </RevenueLegendLabelRow>
            <RevenueLegendValue>
              ${s.data.reduce((sum, v) => sum + v, 0).toLocaleString()}
            </RevenueLegendValue>
          </RevenueLegendItem>
        ))}
      </RevenueLegendRow>

      <ChartCanvasContainer>
        <Line data={chartData} options={options} />
      </ChartCanvasContainer>

      <Modal open={expanded} onCancel={() => setExpanded(false)} footer={null} title="Revenue" width={860}>
        <ModalSubtitle>Revenue vs refunds over time</ModalSubtitle>
        <ModalChartContainer>
          <Line data={chartData} options={options} />
        </ModalChartContainer>
      </Modal>
    </ChartCard>
  );
};

export default RevenueCard;
